/**
 * @file Shared saga catch path: logs the normalized error, signs out on a 401, calls `onError` and snackbars the translated message.
 * An id in the saga's expected-error map logs at `warning`; one mapped to texts opens the refusal dialog instead of the snackbar.
 */
import { put } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import Logger from "../../Logger/Logger";
import Translator from "../../Translation/Translator";
import { NormalizedErrorResponseInterface } from "../../Http/ResponseNormalizer";
import { ActionInterface } from "../../Interface/ActionInterface";

// Expected error id → its refusal dialog's texts; `null` keeps the informative flow.
export type ExpectedErrors = { [errorId: string]: { titleKey: string; bodyKey: string } | null };

export function* ErrorHandlerSequence(err: NormalizedErrorResponseInterface, action: ActionInterface, expectedErrors: ExpectedErrors = {}) {
  const isExpected = Object.prototype.hasOwnProperty.call(expectedErrors, err?.errorId);
  const refusalTexts = isExpected && !err.isValidationError ? expectedErrors[err.errorId] : null;

  function getErrorLevel(err): string {
    if (
        isExpected ||
        err.isValidationError ||
        err.isAuthenticationIssue
      ) {
      return "warning";
    }

    return "error";
  }

  Logger.logError({
    ...err,
    action: action
  }, getErrorLevel(err));

  yield put({
    type: Constants.LOADING_HIDE
  });

  if (refusalTexts) {
    yield put({
      type: Constants.GLOBAL_REFUSAL_DIALOG_OPEN,
      payload: {
        titleKey: refusalTexts.titleKey,
        bodyKey: refusalTexts.bodyKey
      }
    });
  }

  if (err.isAuthenticationIssue === true) {
    yield put({
      type: Constants.PAGE_SIGNOUT_START
    });

    yield put({
      type: Constants.PAGE_CHECK_EMAIL_ADD_SIGNOUT_REASON,
      payload: {
        signoutReason: Translator.trans("be_token_invalid")
      }
    });
  }

  if (action.hasOwnProperty("onError") === true &&
    err.message === "Charge requires at least one relation") {
      action.onError.call(this, err);
      return;
  }

  // @TODO very temporary solution to hide alert window on create/update email page
  if ((action.type === Constants.PAGE_SETTINGS_EMAIL_ACCOUNT_UPDATE_START ||
      action.type === Constants.PAGE_SETTINGS_EMAIL_ACCOUNT_CREATE_START) &&
      action.hasOwnProperty("onError") === true) {
        action.onError.call(this, err);
        return;
      }

  // This technical error for open specific popup
  if ((action.type === Constants.GLOBAL_PAYMENT_LINK_START) &&
    action.hasOwnProperty("onError") === true &&
    err.message === "You cannot pay charge with different type of payment") {
      action.onError.call(this, err);
      return;
  }

  // Should hide this technical error for bad attachment extension
  if ((action.type === Constants.GLOBAL_ATTACHMENT_UPLOAD_START) &&
    action.hasOwnProperty("onError") === true &&
    err.message === "Cannot read property 'indexOf' of undefined") {
      action.onError.call(this, err);
      return;
  }

  // do not need snackbar because we will show popup with an explanation
  if (err.errorId === "statement_data_outdated") {
    return action?.onError?.call(this, err);
  }

  if ((action.type === Constants.GLOBAL_ATTACHMENT_UPLOAD_START) &&
    action.hasOwnProperty("onError") === true &&
    err.message === "getaddrinfo EAI_AGAIN anil-local.s3.eu-central-1.amazonaws.com:443") {
      err.translatedMessage = Translator.trans("check-internet-try-again");
      action.onError.call(this, err);
  }

  if ((action.type === Constants.GLOBAL_UNIT_CREATE_START) &&
    action.hasOwnProperty("onError") === true &&
    err.message === "Unit number already exist") {
      yield put({
        type: Constants.GLOBAL_UNIT_CREATE_FAILURE,
        payload: {
          message: err.message
        }
      });
  }

  if (
    err.isValidationError
  ) {
    const isLabelled = !!action.getLabelFor && err.errorId === "be_ajv_not_validated";

    yield put({
      type: Constants.SNACKBAR_ADD_MESSAGE,
      payload: {
        // An error id without a translation falls back to the generic field message.
        message: isLabelled ?
          Translator.trans("be_ajv_not_validated", { fieldName: action.getLabelFor(err.fields?.[0] || "") }) :
          err.translatedMessage?.indexOf("[MISSING]") === 0 ?
          Translator.trans("be_ajv_not_validated", { fieldName: err.fields?.[0] || "" }) :
          err.translatedMessage,
        type: "error"
      }
    });
  }

  if (action.hasOwnProperty("onError")) {
    action.onError.call(this, err);
  }

  if (action?.payload?.hideSnackbar || refusalTexts) {
    return;
  }

  if (!err.isValidationError) {
    if(action.type === Constants.GLOBAL_PAYMENT_UPDATE_START && err.message === "Can not change class of payment that already linked to the charge"){
      err.translatedMessage = Translator.trans("cannot_change_class_of_payment_that_already_linked_to_the_charge");
    }

    yield put({
      type: Constants.SNACKBAR_ADD_MESSAGE,
      payload: {
        message: err.translatedMessage,
        type: "error"
      }
    });
  }
}
